import moment from 'moment';
import {FaCheckCircle, FaTimesCircle} from 'react-icons/fa';
import EditItemIcon from "../../../assets/edit-item.svg";
import DeleteItemIcon from "../../../assets/delete-item.svg";
import ViewSpaceIcon from "../../../assets/view-space.svg";
import {useMemo, useState} from "react";

const ViewSpaces = ({
                        data,
                        editHandler,
                        deleteHandler,
                        isLoadingDelete,
                        t,
                        onRefresh,
                        isRefreshing,
                        lastUpdatedAt
                    }) => {
    const [previewImage, setPreviewImage] = useState(null);

    const handleImageClick = (imagePath) => setPreviewImage(imagePath);
    const handleCloseImageModal = () => setPreviewImage(null);

    const isUnderReviewFlag = (space) => Boolean(space?.under_review ?? space?.underReview);

    const {sortedSpaces, counts} = useMemo(() => {
        const items = data?.data ?? [];
        let underReview = 0, blocked = 0, unconfirmed = 0;

        const under = [];
        const rest = [];

        for (const s of items) {
            const underFlag = isUnderReviewFlag(s);
            const blockedFlag = s.blocked === true;
            const confirmedFlag = s.confirmed === true;

            if (underFlag) underReview++;
            if (blockedFlag) blocked++;
            if (!confirmedFlag) unconfirmed++;

            (underFlag ? under : rest).push(s);
        }

        return {
            sortedSpaces: [...under, ...rest],
            counts: {underReview, blocked, unconfirmed}
        };
    }, [data]);

    const getRowClasses = (space) => {
        const classes = [];
        if (space.name === '' || space.lng === '' || space.lat === '') classes.push('empty-values');

        const isConfirmed = space.confirmed === true;
        const isBlocked = space.blocked === true;
        const isUnderReview = Boolean(space.under_review);

        if (!isConfirmed) classes.push('unconfirmed');
        if (isBlocked) classes.push('blocked');
        if (isUnderReview) classes.push('under-review');

        return classes.join(' ');
    };
    const getThumbSrc = (space) => {
        const base = data?.images_path ?? '';
        const firstPath = space?.images?.[0]?.path;
        return `${base}${firstPath || 'item-no-image.png'}`;
    };
    const showSummary = (counts.underReview + counts.blocked + counts.unconfirmed) > 0;
    const lastUpdatedLabel = lastUpdatedAt ? moment(lastUpdatedAt).fromNow() : t('never');

    return (
        <div className='table-responsive bg-light p-3 mt-2 spaces-grid-view'>
            {/* Summary banner */}
            {showSummary && (
                <div className="status-summary mb-2 d-flex align-items-center gap-2">
          <span className="chip chip-under-review">
            {t('Under review')}: <strong>{counts.underReview}</strong>
          </span>
                    <span className="chip chip-blocked">
            {t('Blocked')}: <strong>{counts.blocked}</strong>
          </span>
                    <span className="chip chip-unconfirmed">
            {t('Unconfirmed')}: <strong>{counts.unconfirmed}</strong>
          </span>

                    {/* Controls on the right */}
                    <span className="ms-auto d-flex align-items-center gap-2">
                        <span className="text-muted small">
              {t('Last updated')}: {lastUpdatedLabel}
            </span>
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onRefresh}
                    disabled={isRefreshing}
                    title={t('Refresh')}
                >
              {isRefreshing ? (
                  <span className="spinner-border spinner-border-sm"/>
              ) : (
                  t('Refresh')
              )}
            </button>
          </span>
                </div>
            )}

            <table className='table table-sm table-border edit-grid-view'>
                <thead className='border-0'>
                <tr>
                    <th className="th-joined">{t("Joined Date")}</th>
                    <th className="th-thumbnail"></th>
                    <th className="th-name">{t("Name")}</th>
                    <th className="th-address">{t("Address")}</th>
                    <th className="th-confirmed">{t("Confirmed")}</th>
                    <th className="th-blocked">{t("Blocked")}</th>
                    <th className="th-actions">{t("Actions")}</th>
                </tr>
                </thead>

                <tbody>
                {data && data.data ? (
                    sortedSpaces.map((space) => (
                        <tr key={space._id} className={getRowClasses(space)}>
                            <td>{moment(space.createdAt).format('lll')}</td>
                            <td className="product-list-thumbnail">
                                <div
                                    className="zoom-hover-container"
                                    onClick={() => handleImageClick(getThumbSrc(space))}
                                    style={{cursor: 'pointer'}}
                                >
                                    <img
                                        src={getThumbSrc(space)}
                                        alt="Thumbnail"
                                    />
                                </div>
                            </td>
                            <td className='list-item-name'>{space.name}</td>
                            <td className='list-item-address'>{space.address}</td>

                            <td className='list-item-confirmed'>
                                {space.confirmed ? (
                                    <FaCheckCircle className='text-success'/>
                                ) : (
                                    <FaTimesCircle className='text-danger'/>
                                )}
                            </td>
                            <td className='list-item-blocked'>
                                {space.blocked ? (
                                    <FaCheckCircle className='text-success'/>
                                ) : (
                                    <FaTimesCircle className='text-danger'/>
                                )}
                            </td>

                            <td className='list-item-buttons'>
                                <div className='btn-group'>
                                    <button className='btn' onClick={() => editHandler(space)}>
                                        <img src={EditItemIcon} alt="Edit" className="icon-img action-icons-img"/>
                                    </button>

                                    <button
                                        className='btn'
                                        onClick={() => deleteHandler(space._id)}
                                        disabled={isLoadingDelete}
                                    >
                                        {isLoadingDelete ? (
                                            <span className='spinner-border spinner-border-sm'/>
                                        ) : (
                                            <img src={DeleteItemIcon} alt="Delete"
                                                 className="icon-img action-icons-img"/>
                                        )}
                                    </button>

                                    <button
                                        className="btn"
                                        onClick={() => window.open(`/space-view/${space._id}`, '_blank')}
                                    >
                                        <img src={ViewSpaceIcon} alt="View" className="icon-img action-icons-img"/>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan="8">Error: Failed to fetch space data</td>
                    </tr>
                )}
                </tbody>
            </table>

            {previewImage && (
                <div className="image-modal-overlay" onClick={handleCloseImageModal}>
                    <div className="image-modal-content" onClick={e => e.stopPropagation()}>
                        <img src={previewImage} alt="Preview"/>
                        <button className="close-button" onClick={handleCloseImageModal}>×</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewSpaces;
